/**
 * Estrutura de modulos top-level da sidebar do PharuERP.
 *
 * Filosofia atual:
 * - 11 categorias top-level, todas com accordion expandable/colapsavel.
 * - "Legado" agrupa as paginas herdadas do PharuERP (Inicio + Clientes/Planilha +
 *   sub-grupos Escritorio, Fabrica, Projetos Executivos e KPIs). E vendido a
 *   parte (ADR-0102): `requiresModulo` no modulo/grupo/subgrupo vale para os
 *   itens de dentro (ver `herdarModulo`) e a unidade sem o modulo nao ve nada.
 * - "Administracao" agrupa RBAC + Operadores PIN + Integracoes CAD + Sistema +
 *   Kanbans.
 * - "RC (Relacionamento com Cliente)" renomeia a antiga Pos-venda — mais
 *   alinhada com o vocabulario operacional das marcenarias.
 * - Visao Geral fica como leaf direta (sem grupo intermediario).
 *
 * Persistencia: o hook useTopLevelAccordion (em hooks/useTopLevelAccordion.js)
 * salva quais top-levels estao abertos em localStorage. Por padrao, apenas a
 * categoria da rota atual abre.
 */

import {
  FiActivity,
  FiAlertOctagon,
  FiAlertTriangle,
  FiArchive,
  FiAward,
  FiXCircle,
  FiBarChart2,
  FiBookmark,
  FiBookOpen,
  FiPrinter,
  FiBriefcase,
  FiCalendar,
  FiCheckSquare,
  FiClipboard,
  FiClock,
  FiCompass,
  FiCpu,
  FiCrosshair,
  FiDatabase,
  FiDollarSign,
  FiDroplet,
  FiEdit3,
  FiFileText,
  FiFilePlus,
  FiGrid,
  FiHeart,
  FiStar,
  FiHexagon,
  FiHome,
  FiInbox,
  FiLayers,
  FiLifeBuoy,
  FiPackage,
  FiPieChart,
  FiLink,
  FiCreditCard,
  FiLink2,
  FiList,
  FiMap,
  FiMapPin,
  FiMessageCircle,
  FiMessageSquare,
  FiPenTool,
  FiSend,
  FiSettings,
  FiShield,
  FiShoppingBag,
  FiSliders,
  FiTarget,
  FiThumbsUp,
  FiTool,
  FiTrendingUp,
  FiTruck,
  FiScissors,
  FiUnlock,
  FiUploadCloud,
  FiUser,
  FiUserCheck,
  FiUsers,
  FiZap,
} from "react-icons/fi";

import { whatsappHabilitado } from "./whatsappHabilitado";
// Fabrica nao tem equivalente no conjunto Fi; o Fa e o unico com o icone
// classico de industria (chamines), que e o que a sidebar do Legado pede.
import { FaIndustry } from "react-icons/fa";


import {
  MODULO_CENTRAL_PROJETOS,
  MODULO_PLANILHA_LEGADO,
  MODULO_PROJETOS_EXECUTIVOS,
} from "../config/modulos";
import { PHARUCUT_URL, PRODUTO_PHARUCUT } from "../config/produtos";
import { PERMS_ABA_ACESSOS } from "../pages/Acessos/permissoes";
import { peRequireAny } from "../pages/ProjetosExecutivos/pePerms";
import {
  PERMS_HUB_SOLICITACOES,
  PERMS_ROTA_GESTAO_SOLICITACOES,
} from "../services/solicitacoes";

/**
 * Itens de um grupo, incluindo os dos subgrupos (nivel novo de 2026-08-04,
 * criado para "Projetos Executivos" agrupar Analises/Paineis/Base).
 * Todo codigo que VARRE itens (favoritos, breadcrumb, deteccao de rota ativa,
 * flyout) deve usar este helper em vez de `grupo.itens` direto — senao paginas
 * dentro de subgrupos ficam invisiveis para essa funcionalidade.
 */
export function itensDoGrupo(grupo) {
  return [
    ...(grupo.itens || []),
    ...(grupo.subgrupos || []).flatMap((sg) => sg.itens || []),
  ];
}

const MODULES_DECLARADOS = [
  // ============================================================
  // 1. VISÃO GERAL — leaf direta (placeholder)
  // ============================================================
  {
    id: "visao-geral",
    label: "Visão Geral",
    icone: FiCompass,
    requires: [],
    grupos: [
      {
        label: null,
        itens: [
          { label: "Visão Geral", to: "/visao-geral", icone: FiCompass, exact: true },
          // Agenda (ADR-0090; antes "Minhas Pendências"): tarefas e pendências de cada pessoa. Visível a todos.
          { label: "Agenda", to: "/minhas-pendencias", icone: FiCalendar, exact: true },
          // Chat Pharu (ADR-0077): da plataforma, o mesmo chat do PharuCut.
          { label: "Chat", to: "/chat", requires: ["chat:view"], icone: FiMessageCircle, badge: "chatNaoLidas" },
        ],
      },
    ],
  },

  // ============================================================
  // 2. LEGADO — paginas herdadas do PharuERP (a cliente-piloto opera daqui hoje)
  // ============================================================
  {
    id: "legado",
    label: "Legado",
    icone: FiDatabase,
    requires: [],
    // Módulo contratado (ADR-0102): a planilha do Gerenciador vale para o
    // grupo inteiro, menos os subgrupos que declaram o próprio módulo.
    requiresModulo: MODULO_PLANILHA_LEGADO,
    grupos: [
      {
        label: null,
        itens: [
          { label: "Início", to: "/home", requires: ["dashboard:view"], icone: FiHome, exact: true },
          { label: "Clientes", to: "/legado/clientes", requires: ["comercial:view"], icone: FiUsers },
          // Panorama dos ambientes em aberto (substitui o relatório impresso do dono).
          { label: "Relatórios", to: "/legado/relatorios", requires: ["relatorios_legado:view"], icone: FiPieChart },
        ],
      },
      // Redesenho da sidebar do Legado (Igor, 2026-08-04): grupos deixaram de
      // ser cabecalhos EM CAIXA ALTA e viraram itens com icone, no mesmo
      // visual de Inicio/Clientes. `icone` no grupo ativa esse modo no
      // componente Group da Sidebar.
      {
        label: "Escritório",
        icone: FiBriefcase,
        itens: [
          {
            label: "Acompanhamento",
            to: "/acompanhamento",
            requires: ["acompanhamento:view"],
            icone: FiActivity,
          },
          { label: "Liberação", to: "/liberacao", requires: ["liberacao:view"], icone: FiUnlock },
          { label: "Acabamentos", to: "/acabamentos", requires: ["acabamentos:view"], icone: FiCheckSquare },
          { label: "Medição", to: "/medicao", requires: ["medicao:view"], icone: FiCrosshair },
          { label: "Executivo", to: "/executivo", requires: ["executivo:view"], icone: FiLayers },
          // Revisão antes da aprovação (28/10/2026).
          { label: "Revisão", to: "/revisao", requires: ["revisao:view"], icone: FiEdit3 },
          { label: "Aprovação", to: "/aprovacao", requires: ["aprovacao:view"], icone: FiThumbsUp },
          // Revisor de Ferragens saiu daqui para Suprimentos (ADR-0102): lê a
          // lista do DinaBox, não a planilha.
          { label: "PPCP", to: "/ppcp", requires: ["ppcp:view"], icone: FiTarget },
          {
            label: "Área do PCP",
            to: "/legado/area-pcp",
            requires: ["area_pcp:view"],
            icone: FiBookOpen,
          },
          // Folhas A4 de apontamento por lote para os quadros da fábrica.
          { label: "Apontamentos", to: "/legado/apontamentos", requires: ["apontamentos_pcp:view"], icone: FiPrinter },
        ],
      },
      {
        label: "Fábrica",
        icone: FaIndustry,
        itens: [
          // Mapa da Producao substitui o "Planejamento Marcenaria (planilha)".
          { label: "Mapa da Produção", to: "/legado/mapa", requires: ["mapa_producao:view"], icone: FiMap },
          // Fila projetada no ritmo medido: quanto de cada mes ja esta tomado e
          // onde cabe venda. Substitui a "capacidade" fixa do KPIs PPCP.
          {
            label: "Capacidade da Fábrica",
            to: "/legado/capacidade",
            requires: ["capacidade_fabrica:view"],
            icone: FiTrendingUp,
          },
          // B24: "Compras" tambem existe em Suprimentos (Ordens de compra +
          // Requisicoes), com backend real. Este le a planilha.
          { label: "Compras (planilha)", to: "/compras", requires: ["compras:view"], icone: FiShoppingBag },
          { label: "Ferragens", to: "/ferragens", requires: ["ferragens:view"], icone: FiTool },
          { label: "Máquinas", to: "/industrializacao", requires: ["industrializacao:view"], icone: FiCpu },
          { label: "Marcenaria (planilha)", to: "/marcenaria", requires: ["marcenaria:view"], icone: FiHexagon },
          { label: "Terceiros", to: "/terceiros", requires: ["terceiros:view"], icone: FiSend },
          { label: "Pintura & Lacagem", to: "/acabamento", requires: ["acabamento:view"], icone: FiDroplet },
          { label: "Entregas", to: "/entregas", requires: ["entregas:view"], icone: FiTruck },
          { label: "Romaneio (Terceiros)", to: "/romaneios", requires: ["romaneios:view"], icone: FiClipboard },
          { label: "Histórico de Entregas", to: "/obras", requires: ["obras:view"], icone: FiArchive },
        ],
      },
      {
        // Antes eram TRES grupos irmaos ("Proj. Executivos · Analises/Paineis/
        // Base") poluindo a sidebar. Viraram UM item "Projetos Executivos" com
        // subgrupos — o nivel `subgrupos` existe para exatamente este caso.
        // RBAC por página (auditoria 2026-07-02): requireAny = guarda-chuva
        // projetos_executivos:view OU a permissão pe_<slug>:view da página.
        label: "Projetos Executivos",
        icone: FiClipboard,
        itens: [],
        subgrupos: [
          {
            // Central de Projetos (ADR-0089): só aparece com a Central ligada
            // na unidade (`condicao`), menos a configuração, que é onde liga.
            label: "Central de Projetos",
            requiresModulo: MODULO_CENTRAL_PROJETOS,
            itens: [
              { label: "Painel", to: "/central-projetos", requires: ["central_projetos:view"], condicao: "central_ativa", icone: FiGrid },
              { label: "Pendências", to: "/central-projetos/pendencias", requires: ["central_projetos:view"], condicao: "central_ativa", icone: FiCheckSquare },
              { label: "Conversas", to: "/central-projetos/conversas", requires: ["central_projetos:view"], condicao: "central_ativa", icone: FiMessageCircle },
              { label: "Assistente", to: "/central-projetos/assistente", requires: ["central_projetos:assistente"], condicao: "central_ativa", icone: FiMessageSquare },
              { label: "Configuração", to: "/central-projetos/configuracao", requires: ["central_projetos:configurar"], icone: FiSettings },
            ],
          },
          {
            label: "Análises",
            requiresModulo: MODULO_PROJETOS_EXECUTIVOS,
            itens: [
          { label: "Visão geral", to: "/projetos-executivos/analises/visao-geral", requireAny: peRequireAny("visao-geral"), condicao: "pe_visivel", icone: FiGrid },
          { label: "Fila & gargalos", to: "/projetos-executivos/analises/fila-gargalos", requireAny: peRequireAny("fila-gargalos"), condicao: "pe_visivel", icone: FiAlertTriangle },
          { label: "Fluxo & lead time", to: "/projetos-executivos/analises/fluxo-lead-time", requireAny: peRequireAny("fluxo-lead-time"), condicao: "pe_visivel", icone: FiActivity },
          { label: "Retrabalho & aprovação", to: "/projetos-executivos/analises/retrabalho-aprovacao", requireAny: peRequireAny("retrabalho-aprovacao"), condicao: "pe_visivel", icone: FiThumbsUp },
          { label: "Interferências", to: "/projetos-executivos/analises/interferencias", requireAny: peRequireAny("interferencias"), condicao: "pe_visivel", icone: FiCrosshair },
          { label: "Equipe & produtividade", to: "/projetos-executivos/analises/equipe-produtividade", requireAny: peRequireAny("equipe-produtividade"), condicao: "pe_visivel", icone: FiUsers },
          { label: "Vazão mensal", to: "/projetos-executivos/analises/vazao-mensal", requireAny: peRequireAny("vazao-mensal"), condicao: "pe_visivel", icone: FiBarChart2 },
          { label: "Qualidade & dificuldades", to: "/projetos-executivos/analises/qualidade-dificuldades", requireAny: peRequireAny("qualidade-dificuldades"), condicao: "pe_visivel", icone: FiHexagon },
            ],
          },
          {
            label: "Painéis",
            requiresModulo: MODULO_PROJETOS_EXECUTIVOS,
            itens: [
          { label: "Sem definição de acabamento", to: "/projetos-executivos/paineis/sem-acabamento", requireAny: peRequireAny("sem-acabamento"), condicao: "pe_visivel", icone: FiCheckSquare },
          { label: "Sem medição", to: "/projetos-executivos/paineis/sem-medicao", requireAny: peRequireAny("sem-medicao"), condicao: "pe_visivel", icone: FiTarget },
          { label: "Aguardando aprovação", to: "/projetos-executivos/paineis/aguardando-aprovacao", requireAny: peRequireAny("aguardando-aprovacao"), condicao: "pe_visivel", icone: FiClock },
          { label: "Ambientes por projetista", to: "/projetos-executivos/paineis/ambientes-por-projetista", requireAny: peRequireAny("ambientes-por-projetista"), condicao: "pe_visivel", icone: FiPenTool },
          { label: "Detalhamento", to: "/projetos-executivos/paineis/detalhamento", requireAny: peRequireAny("detalhamento"), condicao: "pe_visivel", icone: FiLayers },
          { label: "Versão / retrabalho", to: "/projetos-executivos/paineis/versao-retrabalho", requireAny: peRequireAny("versao-retrabalho"), condicao: "pe_visivel", icone: FiEdit3 },
          { label: "Mensal por envio", to: "/projetos-executivos/paineis/mensal-por-envio", requireAny: peRequireAny("mensal-por-envio"), condicao: "pe_visivel", icone: FiCalendar },
          { label: "Finalização por data", to: "/projetos-executivos/paineis/finalizacao-por-data", requireAny: peRequireAny("finalizacao-por-data"), condicao: "pe_visivel", icone: FiFileText },
            ],
          },
          {
            label: "Base",
            requiresModulo: MODULO_PROJETOS_EXECUTIVOS,
            itens: [
              { label: "Ambientes", to: "/projetos-executivos/ambientes", requireAny: peRequireAny("ambientes"), condicao: "pe_visivel", icone: FiList },
            ],
          },
        ],
      },
      {
        label: "KPIs",
        icone: FiBarChart2,
        itens: [
          { label: "KPIs PPCP (planilha)", to: "/kpis-ppcp", requires: ["kpis_ppcp:view"], icone: FiBarChart2 },
          { label: "KPIs de Tempo", to: "/kpis-tempos", requires: ["kpis_tempos:view"], icone: FiClock },
          { label: "Marceneiros", to: "/marceneiros", requires: ["marceneiros:view"], icone: FiUser },
          // B18: existe um "Financeiro" no ERP (modulo `financeiro-erp`) com
          // o MESMO rotulo. Este le a planilha; aquele le o banco. Enquanto
          // os dois convivem, o nome tem de dizer qual e qual.
          { label: "Financeiro (planilha)", to: "/financeiro", requires: ["financeiro:view"], icone: FiDollarSign },
        ],
      },
    ],
  },

  // ============================================================
  // 3. FINANCEIRO (Fase 13)
  // ============================================================
  {
    id: "financeiro-erp",
    label: "Financeiro",
    icone: FiDollarSign,
    // Sem gate no modulo: cada item se protege, e o modulo aparece se
    // qualquer item aparecer (a Sidebar descarta modulo sem itens). Com
    // `financeiro:view` aqui, quem so emite nota (fiscal:view) ou so cuida
    // de boleto (cobranca:view) perdia o menu inteiro.
    requires: [],
    grupos: [
      {
        label: null,
        itens: [
          {
            label: "Dashboard",
            to: "/financeiro/dashboard",
            requires: ["financeiro:view"],
            icone: FiBarChart2,
          },
          {
            label: "Contas a Pagar",
            to: "/financeiro/contas-pagar",
            requires: ["financeiro:view"],
            icone: FiSend,
          },
          {
            label: "Contas a Receber",
            to: "/financeiro/contas-receber",
            requires: ["financeiro:view"],
            icone: FiInbox,
          },
          {
            label: "Lançamentos",
            to: "/financeiro/lancamentos",
            requires: ["financeiro:view"],
            icone: FiActivity,
          },
          {
            label: "DRE",
            to: "/financeiro/dre",
            requires: ["financeiro:view"],
            icone: FiFileText,
          },
          {
            label: "Fluxo de Caixa",
            to: "/financeiro/fluxo-caixa",
            requires: ["financeiro:view"],
            icone: FiTrendingUp,
          },
          {
            label: "Rentabilidade",
            to: "/financeiro/rentabilidade",
            requires: ["financeiro:view"],
            icone: FiAward,
          },
          {
            label: "Custos indiretos",
            to: "/financeiro/custos-indiretos",
            requires: ["financeiro:view"],
            icone: FiAlertTriangle,
          },
          {
            label: "Comissões",
            to: "/financeiro/comissoes",
            requires: ["financeiro:view"],
            icone: FiUsers,
          },
          {
            label: "Conciliação bancária",
            to: "/financeiro/conciliacao-bancaria",
            requires: ["financeiro:view"],
            icone: FiLink2,
          },
          {
            label: "Integrações bancarias",
            to: "/financeiro/integracoes-bancarias",
            requires: ["financeiro:view"],
            icone: FiLink,
          },
          {
            // B25: a rota `/financeiro/*` exige `financeiro:view`. Com só
            // `cheque:view`, o item aparecia no menu e o clique era
            // recusado — menu que promete o que a rota não entrega.
            label: "Cheques",
            to: "/financeiro/cheques",
            requires: ["financeiro:view", "cheque:view"],
            icone: FiCreditCard,
          },
          {
            label: "Cadastros",
            to: "/financeiro/cadastros",
            requires: ["financeiro:view"],
            icone: FiSliders,
          },
        ],
      },
      {
        // HF-D1: Fiscal vira sub-grupo dentro de Financeiro (era top-level isolado).
        label: "Fiscal",
        itens: [
          {
            label: "Notas Fiscais",
            to: "/fiscal/notas-fiscais",
            requires: ["fiscal:view"],
            icone: FiFileText,
          },
          {
            label: "Configuração",
            to: "/fiscal/configuracao",
            // A rota /fiscal/* exige fiscal:view antes da tela de config.
            requires: ["fiscal:view", "fiscal:configurar"],
            icone: FiSettings,
          },
          {
            label: "Boletos",
            to: "/cobranca/boletos",
            requires: ["cobranca:view"],
            icone: FiFilePlus,
          },
          {
            // V3: a tela existia e só era alcançável por um botão dentro
            // da lista de boletos — quem procurasse a configuração do
            // gateway no menu não achava.
            label: "Configuração de cobrança",
            to: "/cobranca/configuracao",
            // A rota /cobranca/* exige cobranca:view antes da tela de config.
            requires: ["cobranca:view", "cobranca:configurar"],
            icone: FiSettings,
          },
        ],
      },
    ],
  },

  // ============================================================
  // 4. COMERCIAL (Fase 3-5)
  // ============================================================
  {
    id: "comercial",
    label: "Comercial",
    icone: FiBriefcase,
    requires: [],
    grupos: [
      {
        label: null,
        itens: [
          {
            label: "Painel Comercial",
            to: "/comercial/painel",
            requires: ["comercial:metricas"],
            icone: FiBarChart2,
          },
          {
            label: "CRM",
            to: "/crm",
            requires: ["lead:view"],
            icone: FiFileText,
          },
          {
            // Leads ganhos (viraram cliente) — ocultos do board ativo.
            label: "Leads ganhos",
            to: "/crm/ganhos",
            requires: ["lead:view"],
            icone: FiAward,
            exact: true,
          },
          {
            // Leads perdidos — ocultos do board ativo (aba de analise de perda).
            label: "Leads perdidos",
            to: "/crm/perdidos",
            requires: ["lead:view"],
            icone: FiXCircle,
            exact: true,
          },
          {
            // R24: lista dos leads arquivados (mesmo gate do CRM). exact para
            // nao roubar o highlight das demais sub-rotas /crm/*.
            label: "Leads arquivados",
            to: "/crm/arquivados",
            requires: ["lead:view"],
            icone: FiArchive,
            exact: true,
          },
          {
            // Clientes (cadastro central do ERP novo) vive no modulo Comercial.
            label: "Clientes",
            to: "/clientes",
            requires: ["comercial:view"],
            icone: FiUsers,
            exact: true,
          },
          {
            label: "Pedidos",
            to: "/pedidos",
            requires: ["pedido:view"],
            icone: FiClipboard,
          },
          {
            label: "Templates",
            to: "/comercial/templates",
            requires: ["template_orcamento:view"],
            icone: FiBookmark,
          },
          {
            label: "Parceiros",
            to: "/comercial/parceiros",
            requires: ["representante:view"],
            icone: FiUserCheck,
          },
          {
            label: "Formulários",
            to: "/comercial/formularios",
            requires: ["formulario:view"],
            icone: FiFilePlus,
          },
          {
            label: "Configurações",
            to: "/comercial/configuracoes",
            requires: ["comercial:view"],
            icone: FiSliders,
          },
        ],
      },
    ],
  },

  // ============================================================
  // 6. PRODUÇÃO (Fase 8-9)
  // ============================================================
  {
    id: "producao",
    label: "Produção",
    icone: FiCpu,
    // Produção v2 (Sprint P0): o gate desce para os itens — o módulo tinha
    // requires: ["ordem_producao:view"] no topo, o que esconderia as telas
    // novas (gateadas em ambiente_producao/lote/ppcp) de quem não tem a
    // permissão legada. A sidebar já oculta o módulo quando nenhum item é
    // visível, então o comportamento antigo se preserva.
    requires: [],
    grupos: [
      {
        // Produção v2 (BRIEF DEFINITIVO Fase 4) — ordem da F.3.
        label: null,
        itens: [
          {
            label: "Painel de Produção",
            to: "/producao",
            requires: ["ambiente_producao:view"],
            icone: FiGrid,
            exact: true,
          },
          {
            // Lote B (§2.5): atalho mobile do responsável de setor —
            // etapas pendentes/em execução com Iniciar/Concluir inline.
            label: "Minhas etapas",
            to: "/producao/minhas-etapas",
            requires: ["ambiente_producao:view"],
            icone: FiCheckSquare,
          },
          {
            label: "Otimização & Lotes",
            to: "/producao/otimizacao",
            // A rota passa por ExigeProducao (ambiente_producao:view) e a
            // tela lista lotes (lote:view): o menu pede os dois.
            requires: ["ambiente_producao:view", "lote:view"],
            icone: FiLayers,
          },
          {
            // O otimizador de plano de corte é OUTRO produto Pharu, em
            // outro domínio — daí `href` em vez de `to`.
            //
            // Aparece só para quem contratou o PharuCut
            // (`requiresProduto`, conferido contra a mesma lista que monta
            // o `aud` do token) E tem permissão de usá-lo. Até aqui, o
            // cliente que tinha os dois produtos não encontrava um a
            // partir do outro: o frontend do ERP não tinha uma única
            // menção ao PharuCut, em lugar nenhum.
            label: "Otimizador de corte",
            href: `${PHARUCUT_URL}/lotes`,
            requiresProduto: PRODUTO_PHARUCUT,
            requires: ["cut_lote:view"],
            icone: FiScissors,
            pageId: "pharucut-lotes",
          },
          {
            label: "Planej. Marcenaria",
            to: "/producao/marcenaria",
            // ExigeProducao + a tela exige ppcp:view (Marcenaria.jsx).
            requires: ["ambiente_producao:view", "ppcp:view"],
            icone: FiCalendar,
          },
          {
            // Lote D (§2.7): fila de peças reprovadas na Conferência
            // aguardando decisão do PCP (repor/retrabalhar/aceitar).
            // badge = contador da fila (some quando zero).
            label: "Disposição de peças",
            to: "/producao/disposicao",
            requires: ["ambiente_producao:view", "ppcp:view"],
            icone: FiAlertOctagon,
            badge: "disposicaoPendentes",
          },
          {
            label: "KPIs PPCP (DBR)",
            to: "/producao/dbr",
            requires: ["ambiente_producao:view", "ppcp:view"],
            icone: FiTarget,
          },
          {
            label: "Análise de Atrasos",
            to: "/producao/atrasos",
            // ExigeProducao + o dado vem de endpoint ppcp:view.
            requires: ["ambiente_producao:view", "ppcp:view"],
            icone: FiAlertTriangle,
          },
          {
            label: "Configurações da Produção",
            to: "/producao/config",
            // A tela tem modo leitura (ppcp:view) e seções próprias para
            // postos (posto_bipagem:view) e mão de obra (operador:view).
            requireAny: ["ppcp:view", "ppcp:configurar", "posto_bipagem:view", "operador:view"],
            icone: FiSliders,
          },
          {
            // Kiosk fora do shell autenticado (F.3) — a rota é o terminal
            // v2 peça x posto desde o P3.
            label: "Terminal de Bipagem",
            to: "/producao/terminal",
            requires: ["ordem_producao:view"],
            icone: FiCpu,
            externo: true,
          },
        ],
      },
    ],
  },

  // ============================================================
  // 7. SUPRIMENTOS (estoque + compras)
  // ============================================================
  {
    id: "suprimentos",
    label: "Suprimentos",
    icone: FiShoppingBag,
    requires: [],
    grupos: [
      {
        label: null,
        itens: [
          {
            label: "Cadastro de Suprimentos",
            to: "/suprimentos/produtos",
            requires: ["estoque:view"],
            icone: FiArchive,
          },
          {
            // Bloco 4 (12/09/2026): a fila "Ajustes a aprovar" virou aba
            // desta tela; badge = ajustes aguardando (so para quem aprova).
            label: "Movimentos",
            to: "/suprimentos/movimentos",
            requires: ["estoque:view"],
            icone: FiActivity,
            badge: "ajustesAguardando",
          },
          {
            label: "Depósitos",
            to: "/suprimentos/depositos",
            requires: ["estoque:view"],
            icone: FiMapPin,
          },
          {
            label: "Fornecedores",
            to: "/suprimentos/fornecedores",
            requires: ["compras:view"],
            icone: FiBriefcase,
          },
          {
            label: "Requisições de Compra",
            to: "/suprimentos/requisicoes",
            requires: ["compras:view"],
            icone: FiClipboard,
          },
          {
            label: "Ordens de Compra",
            to: "/suprimentos/ordens-compra",
            requires: ["compras:view"],
            icone: FiShoppingBag,
          },
          {
            // Fila unica (ADR-0068): substitui "Produtos pendentes" e
            // "Solicitacoes de cadastro".
            label: "Materiais a cadastrar",
            to: "/suprimentos/materiais-a-cadastrar",
            requires: ["solicitacao_cadastro:view"],
            icone: FiInbox,
          },
          {
            label: "Aproveitamento de Sobras",
            to: "/suprimentos/sobras",
            requires: ["sobra:view"],
            icone: FiLayers,
          },
          {
            label: "Inventário",
            to: "/suprimentos/inventario",
            requires: ["inventario:view"],
            icone: FiCheckSquare,
          },
          {
            label: "Configuração",
            to: "/suprimentos/configuracao",
            requires: ["estoque:view"],
            icone: FiSettings,
          },
          {
            // Conferência da lista de ferragens do DinaBox, que vira a
            // requisição de separação logo abaixo (veio do Legado, ADR-0102).
            label: "Revisor de Ferragens",
            to: "/suprimentos/revisor-ferragens",
            requires: ["revisor_ferragens:view"],
            icone: FiClipboard,
          },
          {
            label: "Requisições de Separação",
            to: "/suprimentos/requisicoes-separacao",
            requires: ["separacao_ferragens:view"],
            icone: FiPackage,
          },
        ],
      },
      {
        label: "Solicitações",
        itens: [
          {
            label: "Solicitações",
            to: "/suprimentos/solicitacoes",
            // RQC (ADR-0074): o escritorio pode ter so a permissao do RQC;
            // a fabrica pode ter so a do tipo (ex.: solicitacao_asm:view).
            requireAny: PERMS_HUB_SOLICITACOES,
            icone: FiEdit3,
          },
          {
            label: "Gestão de Solicitações",
            to: "/suprimentos/gestao-solicitacoes",
            requireAny: PERMS_ROTA_GESTAO_SOLICITACOES,
            icone: FiCheckSquare,
          },
          {
            label: "Painel de Assistências",
            to: "/suprimentos/painel-assistencias",
            // O backend exige `solicitacao:painel` (concedida por migration a
            // quem já geria); o menu segue o mesmo gate para não levar a um 403.
            requireAny: ["solicitacao:painel"],
            icone: FiBarChart2,
          },
        ],
      },
    ],
  },

  // ============================================================
  // 8. TERCEIRIZAÇÃO (Fase 10)
  // ============================================================
  {
    id: "terceirizacao",
    label: "Terceirização",
    icone: FiSend,
    requires: ["terceirizacao:view"],
    grupos: [
      {
        label: null,
        itens: [
          {
            label: "OSEs",
            to: "/terceirizacao/oses",
            requires: ["terceirizacao:view"],
            icone: FiSend,
          },
          {
            // B25: mesma divergência — a rota `/terceirizacao/*` exige
            // `terceirizacao:view`.
            label: "Parceiros",
            to: "/terceirizacao/parceiros",
            requires: ["terceirizacao:view", "parceiro:view"],
            icone: FiUsers,
          },
          {
            label: "Pontualidade",
            to: "/terceirizacao/pontualidade",
            requires: ["terceirizacao:view"],
            icone: FiAward,
          },
          {
            label: "Material em terceiros",
            to: "/terceirizacao/remessas",
            requires: ["terceirizacao:view"],
            icone: FiSend,
          },
        ],
      },
    ],
  },

  // ============================================================
  // 9. LOGÍSTICA (Fase 11)
  // ============================================================
  {
    id: "logistica",
    label: "Logística",
    icone: FiTruck,
    requires: ["logistica:view"],
    grupos: [
      {
        label: null,
        itens: [
          {
            label: "Cargas",
            to: "/logistica/cargas",
            requires: ["logistica:view"],
            icone: FiTruck,
          },
          {
            label: "Entregas",
            to: "/logistica/entregas",
            requires: ["logistica:view"],
            icone: FiSend,
          },
          {
            label: "Instalações",
            to: "/logistica/instalacoes",
            requires: ["logistica:view"],
            icone: FiTool,
          },
          {
            label: "Cadastros",
            to: "/logistica/cadastros",
            requires: ["logistica:view"],
            icone: FiUsers,
          },
          {
            // A tela existia só por URL; quem planeja rotas não achava onde
            // configurar o provedor de roteirização.
            label: "Configuração",
            to: "/logistica/configuracao",
            requires: ["logistica:view", "logistica:planejar"],
            icone: FiSettings,
          },
        ],
      },
    ],
  },

  // ============================================================
  // 10. RC — Relacionamento com Cliente (Fase 12, renomeada de Pos-venda)
  // ============================================================
  {
    id: "rc",
    label: "RC (Relacionamento com Cliente)",
    icone: FiLifeBuoy,
    requires: [],
    grupos: [
      {
        label: null,
        itens: [
          {
            label: "Assistências",
            to: "/posvenda/assistencias",
            requires: ["assistencia:view"],
            icone: FiHeart,
          },
          {
            label: "Kanban",
            to: "/posvenda/assistencias/kanban",
            requires: ["assistencia:view"],
            icone: FiGrid,
          },
          {
            label: "Satisfação (NPS)",
            to: "/posvenda/satisfacao",
            requires: ["satisfacao:view"],
            icone: FiStar,
          },
        ],
      },
    ],
  },

  // ============================================================
  // 10b. WHATSAPP — Atendimento via WhatsApp Web (ADR-0034)
  // Inbox/Instancias/Agendamentos/Configuracoes. Badge global de
  // nao-lidas via WhatsappNaoLidasProvider.
  //
  // GATEADO (Fase 9, achado M-TRA-12 "WhatsApp e fachada"): enquanto o
  // servico Node nao estiver no ar, o modulo nao aparece — o sistema nao
  // deve anunciar uma capacidade que nao tem. Ligar com
  // REACT_APP_WHATSAPP_ENABLED=true.
  // ============================================================
  {
    id: "whatsapp",
    oculto: !whatsappHabilitado(),
    label: "WhatsApp",
    icone: FiMessageCircle,
    badge: "naoLidasWhatsapp",
    requires: ["whatsapp:atender"],
    grupos: [
      {
        label: null,
        itens: [
          {
            label: "Inbox",
            to: "/whatsapp/inbox",
            icone: FiInbox,
            requires: ["whatsapp:atender"],
          },
          {
            label: "Instâncias",
            to: "/whatsapp/instancias",
            icone: FiSend,
            requires: ["whatsapp:manage"],
          },
          {
            label: "Agendamentos",
            to: "/whatsapp/agendamentos",
            icone: FiClock,
            requires: ["whatsapp:atender"],
          },
          {
            label: "Configurações",
            to: "/whatsapp/configuracoes",
            icone: FiSettings,
            requires: ["whatsapp:manage"],
          },
        ],
      },
    ],
  },

  // ============================================================
  // 11. ADMINISTRAÇÃO — RBAC + Operadores PIN + Integracoes + Sistema + Kanbans
  // ============================================================
  {
    id: "administracao",
    label: "Administração",
    icone: FiShield,
    requires: [],
    grupos: [
      {
        label: "RBAC",
        itens: [
          // Auditoria RBAC 2026-10-08: as telas abrem pela permissão de cada
          // parte (eram só do Master, com as permissões concedíveis à toa).
          { label: "Acessos", to: "/configuracoes/acessos", requireAny: PERMS_ABA_ACESSOS.hub, icone: FiShield },
          // Cargos e Auditoria sao sub-rotas dentro de /configuracoes/acessos/* —
          // referenciados aqui para visibilidade direta na sidebar.
          {
            label: "Cargos",
            to: "/configuracoes/acessos/cargos",
            requires: PERMS_ABA_ACESSOS.cargos,
            icone: FiUserCheck,
          },
          {
            // M-TRA-2: `auditoria:view` é concedível no catálogo e gateia
            // o backend corretamente — mas a tela era master-only em duas
            // camadas independentes. Conceder a permissão não dava acesso
            // a nada: o contador que precisa conferir um lançamento
            // continuava dependendo de um Master abrir para ele.
            label: "Auditoria",
            to: "/configuracoes/acessos/auditoria",
            requires: ["auditoria:view"],
            icone: FiList,
          },
        ],
      },
      {
        label: "Produção",
        itens: [
          {
            // A14: apontava para `/configuracoes/acessos/operadores`, rota
            // que nunca existiu — e o router de Acessos nem tem catch-all,
            // então o clique abria um outlet vazio. O CRUD de operadores
            // (com PIN) vive na configuração da produção.
            label: "Operadores PIN",
            to: "/producao/config",
            requires: ["operador:view"],
            icone: FiUsers,
          },
        ],
      },
      {
        label: "Integrações",
        itens: [
          {
            label: "Marketplace",
            to: "/configuracoes/integracoes",
            // R25 (seguranca): catalogo administrativo global de integracoes.
            // Sem gate (`requires: []`) vazava o modulo "Administracao" inteiro
            // para qualquer cargo (ex.: Gestor Comercial). Cada integracao tem
            // item proprio gateado (CAD, E-mail) e tela propria por dominio.
            masterOnly: true,
            icone: FiLink,
          },
          {
            label: "Integrações CAD",
            to: "/configuracoes/integracoes-cad",
            requires: ["integracao_cad:view"],
            icone: FiLink2,
          },
          {
            label: "E-mail (gateway)",
            to: "/emails/configuracao",
            requires: ["email:view"],
            icone: FiSend,
          },
          {
            label: "E-mails enviados",
            to: "/emails/historico",
            requires: ["email:view"],
            icone: FiInbox,
          },
          {
            label: "Webhooks",
            to: "/webhooks",
            requires: ["webhook:view"],
            icone: FiZap,
          },
        ],
      },
      {
        label: "Sistema",
        itens: [
          {
            label: "Importações",
            to: "/configuracoes/importacoes",
            requireAny: ["importacao:executar", "importacao:view_historico"],
            icone: FiUploadCloud,
          },
          // R25 (seguranca): o hub /configuracoes (Perfil/Seguranca/Aparencia/
          // Notificacoes) ja e acessivel pelo menu do usuario (avatar no Header
          // → Perfil / Configuracoes). Mante-lo aqui, SEM gate, fazia o modulo
          // "Administracao" aparecer para qualquer cargo (ex.: Gestor Comercial,
          // que so tem comercial:*). Removido da sidebar; acesso preservado via
          // UserMenu. "Unidades" (CRUD multi-tenant) continua sendo feature futura.
        ],
      },
      {
        label: "Boards",
        itens: [
          {
            label: "Kanbans",
            to: "/kanbans",
            requires: ["kanban:view"],
            icone: FiGrid,
          },
        ],
      },
    ],
  },
];

function comModulo(item, modulo) {
  return item.requiresModulo || !modulo ? item : { ...item, requiresModulo: modulo };
}

/**
 * Desce o `requiresModulo` do módulo para o grupo, do grupo para o subgrupo e
 * daí para cada item (o mais próximo vence). Assim todo consumidor que olha
 * o ITEM (Sidebar, favoritos, `podeVerRota`) enxerga o módulo exigido sem
 * conhecer a hierarquia — e o módulo é declarado uma vez por nível.
 */
export function herdarModulo(modulos) {
  return modulos.map((modulo) => ({
    ...modulo,
    grupos: modulo.grupos.map((grupo) => {
      const doGrupo = grupo.requiresModulo || modulo.requiresModulo;
      return {
        ...grupo,
        itens: (grupo.itens || []).map((item) => comModulo(item, doGrupo)),
        ...(grupo.subgrupos
          ? {
              subgrupos: grupo.subgrupos.map((sg) => {
                const doSubgrupo = sg.requiresModulo || doGrupo;
                return { ...sg, itens: (sg.itens || []).map((item) => comModulo(item, doSubgrupo)) };
              }),
            }
          : {}),
      };
    }),
  }));
}

/**
 * Os módulos visíveis nesta instalação.
 *
 * O filtro por `oculto` vive AQUI, e não em cada consumidor, porque são
 * três (Sidebar, Breadcrumb e Atalhos Rápidos) — deixar a decisão em cada
 * um garantiria que algum ficasse para trás e mostrasse o módulo que os
 * outros escondem.
 */
export const MODULES = herdarModulo(MODULES_DECLARADOS.filter((m) => !m.oculto));

/**
 * O item aparece para esta sessão? Fonte única do gate de um destino do menu
 * (Sidebar, favoritos, atalhos). Ordem: contrato (produto e módulo, sem
 * bypass de Master) → Master-only → condição da unidade → permissões.
 *
 * `ctx`: { hasAllPermissions, hasAnyPermission, isMaster, temProduto,
 * temModulo, estadoCentral } — os helpers do AuthContext (que já aplicam o
 * bypass de Master nas permissões) e o estado da Central.
 */
export function itemVisivel(item, ctx = {}) {
  const { hasAllPermissions, hasAnyPermission, isMaster = false, temProduto, temModulo, estadoCentral } = ctx;
  if (item.requiresProduto && !temProduto?.(item.requiresProduto)) return false;
  if (item.requiresModulo && !temModulo?.(item.requiresModulo)) return false;
  if (item.masterOnly && !isMaster) return false;
  if (item.condicao === "central_ativa" && !estadoCentral?.ativa) return false;
  if (item.condicao === "pe_visivel" && estadoCentral?.esconder_projetos_executivos) return false;
  const hasAll = typeof hasAllPermissions === "function" ? hasAllPermissions : () => false;
  const hasAny = typeof hasAnyPermission === "function" ? hasAnyPermission : () => false;
  if (Array.isArray(item.requires) && item.requires.length > 0 && !hasAll(item.requires)) return false;
  if (Array.isArray(item.requireAny) && item.requireAny.length > 0 && !isMaster && !hasAny(item.requireAny)) {
    return false;
  }
  return true;
}

function moduloVisivel(modulo, { hasAllPermissions, isMaster = false } = {}) {
  if (!Array.isArray(modulo.requires) || modulo.requires.length === 0) return true;
  if (isMaster) return true;
  return typeof hasAllPermissions === "function" && hasAllPermissions(modulo.requires);
}

/**
 * Os módulos com só os itens que a sessão vê; grupo, subgrupo e módulo que
 * ficam vazios somem (ex.: Legado inteiro para a unidade sem os módulos dele).
 */
export function modulosVisiveis(ctx, modulos = MODULES) {
  return modulos
    .filter((modulo) => moduloVisivel(modulo, ctx))
    .map((modulo) => ({
      ...modulo,
      grupos: modulo.grupos
        .map((g) => ({
          ...g,
          itens: (g.itens || []).filter((item) => itemVisivel(item, ctx)),
          subgrupos: (g.subgrupos || [])
            .map((sg) => ({ ...sg, itens: (sg.itens || []).filter((item) => itemVisivel(item, ctx)) }))
            .filter((sg) => sg.itens.length > 0),
        }))
        .filter((g) => g.itens.length > 0 || g.subgrupos.length > 0),
    }))
    .filter((m) => m.grupos.length > 0);
}

// PAGINAS_GLOBAIS agora ficam DENTRO da categoria "Legado". Sem itens flat acima
// dos modulos. Exportado vazio para retro-compat — codigo antigo que importa
// nao quebra.
export const PAGINAS_GLOBAIS = [];

export const MODULO_DEFAULT_ID = "legado";

export function moduloDaRota(pathname) {
  for (const modulo of MODULES) {
    for (const grupo of modulo.grupos) {
      for (const item of itensDoGrupo(grupo)) {
        if (pathname === item.to || pathname.startsWith(`${item.to}/`)) {
          return modulo.id;
        }
      }
    }
  }
  return null;
}

/**
 * Resolve o ITEM da sidebar cujo `to` casa com a rota (prefixo mais
 * especifico vence — ex.: "/producao/terminal" casa com "/producao" e
 * "/producao/terminal"; retorna o segundo). null se a rota nao esta no
 * catalogo.
 */
export function itemDaRota(pathname) {
  let melhor = null;
  for (const modulo of MODULES) {
    for (const grupo of modulo.grupos) {
      for (const item of itensDoGrupo(grupo)) {
        if (pathname === item.to || pathname.startsWith(`${item.to}/`)) {
          if (!melhor || item.to.length > melhor.to.length) melhor = item;
        }
      }
    }
  }
  return melhor;
}

/**
 * Gate de visibilidade por rota — mesmo criterio do `itemVisivel` da sidebar
 * (modulo/produto contratado, masterOnly, requires, requireAny), reusando este
 * catalogo como fonte unica das permissoes por destino (REF-31: atalhos da
 * Visao Geral).
 *
 * Rota fora do catalogo => false: nao assumimos acesso a destino que a
 * sidebar nao conhece.
 */
export function podeVerRota(pathname, { hasAllPermissions, isMaster = false, temModulo, temProduto } = {}) {
  const item = itemDaRota(pathname);
  if (!item) return false;
  // Contrato antes do Master: módulo/produto não contratado não abre nem para ele.
  if (item.requiresModulo && !temModulo?.(item.requiresModulo)) return false;
  if (item.requiresProduto && !temProduto?.(item.requiresProduto)) return false;
  if (item.masterOnly && !isMaster) return false;
  if (isMaster) return true;
  const hasAll =
    typeof hasAllPermissions === "function" ? hasAllPermissions : () => false;
  if (Array.isArray(item.requires) && item.requires.length > 0 && !hasAll(item.requires)) {
    return false;
  }
  if (
    Array.isArray(item.requireAny) &&
    item.requireAny.length > 0 &&
    !item.requireAny.some((codigo) => hasAll([codigo]))
  ) {
    return false;
  }
  return true;
}
